import React, { useState, useEffect } from 'react';
import './Overview.css';

// Mock data for the charts and statistics
const mockPortfolioData = [
  { date: 'Jan', value: 12400 },
  { date: 'Feb', value: 14800 },
  { date: 'Mar', value: 13200 },
  { date: 'Apr', value: 15600 },
  { date: 'May', value: 14900 },
  { date: 'Jun', value: 17800 },
  { date: 'Jul', value: 16500 },
  { date: 'Aug', value: 19200 },
  { date: 'Sep', value: 21000 },
  { date: 'Oct', value: 22400 },
  { date: 'Nov', value: 20800 },
  { date: 'Dec', value: 24500 },
];

const mockAssets = [
  { id: 1, name: 'Bitcoin', symbol: 'BTC', amount: 0.45, value: 18450, change: 2.4, color: '#F7931A' },
  { id: 2, name: 'Ethereum', symbol: 'ETH', amount: 3.25, value: 6825, change: -1.2, color: '#627EEA' },
  { id: 3, name: 'Cardano', symbol: 'ADA', amount: 2150, value: 2150, change: 5.8, color: '#0033AD' },
  { id: 4, name: 'Solana', symbol: 'SOL', amount: 18.7, value: 1870, change: 8.1, color: '#00FFA3' },
  { id: 5, name: 'Polkadot', symbol: 'DOT', amount: 145, value: 1450, change: -0.7, color: '#E6007A' },
];

const mockTransactions = [
  { id: 1, type: 'buy', asset: 'Bitcoin', amount: 0.05, price: 41000, total: 2050, time: '2 hours ago' },
  { id: 2, type: 'sell', asset: 'Ethereum', amount: 1.2, price: 2800, total: 3360, time: 'Yesterday' },
  { id: 3, type: 'buy', asset: 'Solana', amount: 12, price: 105, total: 1260, time: '3 days ago' },
  { id: 4, type: 'transfer', asset: 'USD', amount: 1000, price: 1, total: 1000, time: '5 days ago' },
];

const Overview = () => {
  const [activeTimeframe, setActiveTimeframe] = useState('1M');
  const [portfolioValue, setPortfolioValue] = useState(0);
  const [portfolioChange, setPortfolioChange] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [showWelcome, setShowWelcome] = useState(true);

  // Calculate total portfolio value from assets
  useEffect(() => {
    const calculatePortfolio = () => {
      setIsLoading(true);

      // Calculate values immediately to prevent long loading
      const totalValue = mockAssets.reduce((sum, asset) => sum + asset.value, 0);
      setPortfolioValue(totalValue);

      // Calculate weighted average change
      const totalChange = mockAssets.reduce((sum, asset) => sum + (asset.change * asset.value), 0);
      setPortfolioChange((totalChange / totalValue).toFixed(2));

      // Short loading time for visual feedback
      setTimeout(() => {
        setIsLoading(false);
      }, 500);
    };

    calculatePortfolio();

    // Hide welcome message after 5 seconds
    const welcomeTimer = setTimeout(() => {
      setShowWelcome(false);
    }, 5000);

    return () => clearTimeout(welcomeTimer);
  }, []);

  // Format currency
  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value);
  };

  // Calculate the maximum value for the chart
  const maxValue = Math.max(...mockPortfolioData.map(item => item.value)) * 1.1;

  return (
    <div className="overview-container">
      {showWelcome && (
        <div className="welcome-banner">
          <div className="welcome-content">
            <h2>Welcome to your VDEX Dashboard</h2>
            <p>Track your portfolio, manage your assets, and stay updated with the latest market trends.</p>
            <button onClick={() => setShowWelcome(false)}>Get Started</button>
          </div>
        </div>
      )}

      <div className="overview-header">
        <div className="portfolio-summary">
          <h1>Portfolio Overview</h1>
          <div className="portfolio-value">
            {isLoading ? (
              <div className="skeleton-loader value-loader"></div>
            ) : (
              <>
                <h2>{formatCurrency(portfolioValue)}</h2>
                <span className={portfolioChange >= 0 ? 'positive-change' : 'negative-change'}>
                  {portfolioChange >= 0 ? '+' : ''}{portfolioChange}%
                </span>
              </>
            )}
          </div>
        </div>

        <div className="timeframe-selector">
          <button
            className={activeTimeframe === '1D' ? 'active' : ''}
            onClick={() => setActiveTimeframe('1D')}
          >
            1D
          </button>
          <button
            className={activeTimeframe === '1W' ? 'active' : ''}
            onClick={() => setActiveTimeframe('1W')}
          >
            1W
          </button>
          <button
            className={activeTimeframe === '1M' ? 'active' : ''}
            onClick={() => setActiveTimeframe('1M')}
          >
            1M
          </button>
          <button
            className={activeTimeframe === '3M' ? 'active' : ''}
            onClick={() => setActiveTimeframe('3M')}
          >
            3M
          </button>
          <button
            className={activeTimeframe === '1Y' ? 'active' : ''}
            onClick={() => setActiveTimeframe('1Y')}
          >
            1Y
          </button>
          <button
            className={activeTimeframe === 'ALL' ? 'active' : ''}
            onClick={() => setActiveTimeframe('ALL')}
          >
            ALL
          </button>
        </div>
      </div>

      <div className="chart-container">
        {isLoading ? (
          <div className="skeleton-loader chart-loader"></div>
        ) : (
          <div className="portfolio-chart">
            <div className="chart-y-axis">
              {[0, 1, 2, 3, 4].map((index) => (
                <div key={index} className="y-axis-label">
                  {formatCurrency(maxValue - (index * (maxValue / 4)))}
                </div>
              ))}
            </div>
            <div className="chart-content">
              <div className="chart-grid">
                {[0, 1, 2, 3, 4].map((index) => (
                  <div key={index} className="grid-line"></div>
                ))}
              </div>
              <div className="chart-bars">
                {mockPortfolioData.map((item, index) => (
                  <div key={index} className="chart-bar-container">
                    <div
                      className="chart-bar"
                      style={{
                        height: `${(item.value / maxValue) * 100}%`,
                        backgroundColor: `rgba(0, 255, 255, ${0.3 + (index / mockPortfolioData.length) * 0.7})`
                      }}
                    >
                      <div className="bar-tooltip">
                        <strong>{formatCurrency(item.value)}</strong>
                        <span>{item.date}</span>
                      </div>
                    </div>
                    <div className="chart-x-label">{item.date}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="dashboard-grid">
        <div className="assets-panel">
          <div className="panel-header">
            <h2>Your Assets</h2>
            <button className="action-button">
              <span className="action-icon">+</span> Add Assets
            </button>
          </div>

          {isLoading ? (
            Array(5).fill(0).map((_, index) => (
              <div key={index} className="skeleton-loader asset-loader"></div>
            ))
          ) : (
            <div className="assets-list">
              {mockAssets.map((asset) => (
                <div key={asset.id} className="asset-item">
                  <div className="asset-icon" style={{ backgroundColor: asset.color }}>
                    {asset.symbol.charAt(0)}
                  </div>
                  <div className="asset-details">
                    <div className="asset-name">
                      <h3>{asset.name}</h3>
                      <span className="asset-symbol">{asset.symbol}</span>
                    </div>
                    <div className="asset-amount">
                      {asset.amount} {asset.symbol}
                    </div>
                  </div>
                  <div className="asset-value">
                    <div className="value">{formatCurrency(asset.value)}</div>
                    <div className={`change ${asset.change >= 0 ? 'positive' : 'negative'}`}>
                      {asset.change >= 0 ? '+' : ''}{asset.change}%
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="panel-footer">
            <button className="view-all-button">View All Assets</button>
          </div>
        </div>

        <div className="transactions-panel">
          <div className="panel-header">
            <h2>Recent Transactions</h2>
            <div className="transaction-actions">
              <button className="action-button">
                <span className="action-icon">↑</span> Send
              </button>
              <button className="action-button">
                <span className="action-icon">↓</span> Receive
              </button>
              <button className="action-button">
                <span className="action-icon">↔</span> Swap
              </button>
            </div>
          </div>

          {isLoading ? (
            Array(4).fill(0).map((_, index) => (
              <div key={index} className="skeleton-loader transaction-loader"></div>
            ))
          ) : (
            <div className="transactions-list">
              {mockTransactions.map((transaction) => (
                <div key={transaction.id} className="transaction-item">
                  <div className={`transaction-icon ${transaction.type}`}>
                    {transaction.type === 'buy' && '↑'}
                    {transaction.type === 'sell' && '↓'}
                    {transaction.type === 'transfer' && '↔'}
                  </div>
                  <div className="transaction-details">
                    <div className="transaction-title">
                      <h3>
                        {transaction.type === 'buy' && 'Bought '}
                        {transaction.type === 'sell' && 'Sold '}
                        {transaction.type === 'transfer' && 'Transferred '}
                        {transaction.asset}
                      </h3>
                      <span className="transaction-time">{transaction.time}</span>
                    </div>
                    <div className="transaction-info">
                      {transaction.amount} {transaction.asset} {transaction.type !== 'transfer' && `@ ${formatCurrency(transaction.price)}`}
                    </div>
                  </div>
                  <div className="transaction-value">
                    <div className={`value ${transaction.type}`}>
                      {transaction.type === 'buy' && '-'}
                      {transaction.type === 'sell' && '+'}
                      {formatCurrency(transaction.total)}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="panel-footer">
            <button className="view-all-button">View All Transactions</button>
          </div>
        </div>

        <div className="market-panel">
          <div className="panel-header">
            <h2>Market Trends</h2>
            <button className="refresh-button">
              <span className="refresh-icon">↻</span>
            </button>
          </div>

          {isLoading ? (
            <div className="skeleton-loader market-loader"></div>
          ) : (
            <div className="market-content">
              <div className="market-stats">
                <div className="market-stat-item">
                  <div className="stat-label">Market Cap</div>
                  <div className="stat-value">$2.14T</div>
                  <div className="stat-change positive">+1.2%</div>
                </div>
                <div className="market-stat-item">
                  <div className="stat-label">24h Volume</div>
                  <div className="stat-value">$84.5B</div>
                  <div className="stat-change negative">-3.8%</div>
                </div>
                <div className="market-stat-item">
                  <div className="stat-label">BTC Dominance</div>
                  <div className="stat-value">42.3%</div>
                  <div className="stat-change positive">+0.5%</div>
                </div>
              </div>

              <div className="market-movers">
                <h3>Top Movers</h3>
                <div className="movers-list">
                  <div className="mover-item positive">
                    <div className="mover-name">Solana (SOL)</div>
                    <div className="mover-change">+12.4%</div>
                  </div>
                  <div className="mover-item positive">
                    <div className="mover-name">Avalanche (AVAX)</div>
                    <div className="mover-change">+8.7%</div>
                  </div>
                  <div className="mover-item negative">
                    <div className="mover-name">Dogecoin (DOGE)</div>
                    <div className="mover-change">-5.2%</div>
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="panel-footer">
            <button className="view-all-button">View Market Data</button>
          </div>
        </div>

        <div className="news-panel">
          <div className="panel-header">
            <h2>Latest News</h2>
          </div>

          {isLoading ? (
            Array(3).fill(0).map((_, index) => (
              <div key={index} className="skeleton-loader news-loader"></div>
            ))
          ) : (
            <div className="news-list">
              <div className="news-item">
                <div className="news-source">CoinDesk</div>
                <h3 className="news-title">Bitcoin Surpasses $45,000 as Institutional Interest Grows</h3>
                <div className="news-time">2 hours ago</div>
              </div>
              <div className="news-item">
                <div className="news-source">CryptoNews</div>
                <h3 className="news-title">Ethereum Layer 2 Solutions See Record Adoption</h3>
                <div className="news-time">5 hours ago</div>
              </div>
              <div className="news-item">
                <div className="news-source">Bloomberg</div>
                <h3 className="news-title">Regulatory Clarity Brings New Wave of Crypto Investment</h3>
                <div className="news-time">Yesterday</div>
              </div>
            </div>
          )}

          <div className="panel-footer">
            <button className="view-all-button">View All News</button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Overview;
